/**
 * Tiered search-result ranker for TMDB results.
 *
 * TMDB's search uses n-gram from the START of titles — no fuzzy matching,
 * no typo tolerance, and multi-word queries match the verbatim substring.
 * That's why the dropdown felt bad: "intersteler" returned nothing,
 * "dark night" missed "The Dark Knight", "tom cruise mission" returned 0.
 *
 * This helper scores any candidate against a query so we can:
 *  - keep exact / prefix / "all words present" results at the top,
 *  - tolerate typos via Sørensen-Dice on character bigrams (cheap + good),
 *  - still defer to popularity inside each tier.
 *
 * The score is a single number; higher = better. Callers sort descending.
 */

const norm = (s: string) =>
  (s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const bigrams = (s: string): Set<string> => {
  const out = new Set<string>();
  if (s.length < 2) {
    if (s) out.add(s);
    return out;
  }
  for (let i = 0; i < s.length - 1; i++) out.add(s.slice(i, i + 2));
  return out;
};

/** Sørensen-Dice coefficient (0–1) on character bigrams. */
const dice = (a: string, b: string): number => {
  const A = bigrams(a);
  const B = bigrams(b);
  if (A.size === 0 || B.size === 0) return 0;
  let inter = 0;
  for (const g of A) if (B.has(g)) inter++;
  return (2 * inter) / (A.size + B.size);
};

/**
 * Score a candidate title against a query.
 *
 * Tier layout (relevance):
 *   2000  exact title match
 *   1600  title starts with the full query
 *   1200  every query word appears in the title (any order)
 *    900  title contains the query as a substring
 *    600  fuzzy: dice(title, query) >= 0.6
 *    300  fuzzy: dice(title, query) >= 0.45
 *      0  otherwise
 *
 * Within a tier, quality decides: a vote_count bonus (0–120, log scale — the
 * single best junk-vs-real signal TMDB exposes) plus a popularity bonus
 * (0–60). Tiers are spaced 300+ apart so quality can never jump a tier.
 *
 * Junk gate: below the prefix tiers, movie/TV results that essentially nobody
 * has rated (vote_count < 3 AND popularity < 5) score 0 — these are the
 * shovelware entries that used to flood fuzzy/substring matches. Exact and
 * prefix matches always survive so obscure-but-intended titles stay findable.
 */
export interface RankableCandidate {
  title?: string | null;
  name?: string | null;
  popularity?: number;
  vote_count?: number;
  release_date?: string | null;
  first_air_date?: string | null;
}

export function scoreCandidate(item: RankableCandidate, query: string): number {
  const q = norm(query);
  if (!q) return 0;
  const title = norm(item.title || item.name || '');
  if (!title) return 0;

  let tier = 0;

  if (title === q) {
    tier = 2000;
  } else if (title.startsWith(q)) {
    tier = 1600;
  } else {
    const words = q.split(' ').filter(Boolean);
    if (words.length > 1 && words.every((w) => title.includes(w))) {
      tier = 1200;
    } else if (title.includes(q)) {
      tier = 900;
    } else if (q.length >= 3) {
      // Fuzzy only for queries long enough to carry signal
      const d = dice(title, q);
      if (d >= 0.6) tier = 600;
      else if (d >= 0.45) tier = 300;
    }
  }

  if (tier === 0) return 0;

  const votes = item.vote_count;
  const isMedia = votes !== undefined || item.release_date !== undefined || item.first_air_date !== undefined;

  // Junk gate (media only — people carry no vote_count)
  if (isMedia && tier <= 1200 && (votes ?? 0) < 3 && (item.popularity ?? 0) < 5) {
    return 0;
  }

  const quality = Math.min(Math.log10((votes ?? 0) + 1) * 30, 120); // 0–120
  const pop = (Math.min(item.popularity ?? 0, 200) / 200) * 60; // 0–60
  return tier + quality + pop;
}

/** Sort candidates in-place by descending score; drop zero-score items. */
export function rankCandidates<T extends RankableCandidate>(items: T[], query: string): T[] {
  return items
    .map((item) => ({ item, score: scoreCandidate(item, query) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.item);
}
